import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  updateBookingStepAction,
  updateBookingSubStepAction,
} from '../../../data/actions/bookingAction';
import { bookingPropTypes } from '../../../data/models/Booking';
import { addonGroupsPropTypes, experiencesPropTypes } from '../../../data/models/Experience';
import { PageLayout } from '../../../layouts/PageLayout';
import { WidgetLayout } from '../../../layouts/WidgetLayout';
import { getAddonGroupsForBooking } from '../../../utils/bookingHelpers';
import { isEmpty } from '../../../utils/helpers';
import AddonGroup from './AddonGroup';

/**
 * Add-on screen
 *
 * Since there can be multiple sets of addons, we render a sub-screen for each addon group.
 * e.g. choice of bottle of wine could be one addon (group), and choice of cheese could be another
 *
 * @param booking
 * @param restaurant
 * @param updateBookingStep
 * @returns {JSX.Element}
 * @constructor
 */
const Addons = ({
  booking,
  experiences,
  addonGroups,
  updateBookingStep,
  updateBookingSubStep,
  beforeDetails,
  nextStep, // Where to go after addons are chosen
}) => {
  // Get list of addons to show
  const addonGroupsToShow = useMemo(() => {
    const addons = getAddonGroupsForBooking(booking, addonGroups);

    if (beforeDetails) {
      return addons.beforeDetails.filter((addonGroup) => !isEmpty(addonGroup?.addons));
    }

    return addons.afterDetails.filter((addonGroup) => !isEmpty(addonGroup?.addons));
  }, [addonGroups.data, experiences.data, booking?.selectedExperiences]);

  // The addon group currently showing
  // If going back from next page, show the last addon group
  // const [addonGroupIndex, setAddonGroupIndex] = useState(booking?.subStep ?? 0);

  // Scroll to top whenever the sub-page changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [booking?.subStep]);

  const activeAddonGroup = addonGroupsToShow[booking?.subStep ?? 0];

  // Accommodation logic is different. This addon group is accommodation if it's the first addon in an accommodation experience
  const isAccommodationAddonGroup = booking?.selectedExperiences?.some(
    (experience) =>
      experience?.experience?.experienceTypeSettings?.accommodation &&
      experience?.experience?.addonGroups?.[0]?.id === activeAddonGroup?.id,
  );

  // Load the next addon group, or the next page
  const handleNextPage = () => {
    const newIndex = booking?.subStep + 1;

    // Go to the next page or screen
    if (newIndex >= addonGroupsToShow.length) {
      updateBookingStep(nextStep);
    } else {
      // Update the substep for the navigation
      updateBookingSubStep(newIndex);
    }
  };

  return (
    <PageLayout>
      <WidgetLayout showDetails>
        <AddonGroup
          addonGroup={activeAddonGroup}
          onNext={handleNextPage}
          groupIndex={booking?.subStep ?? 0}
          isAccommodation={isAccommodationAddonGroup}
        />
      </WidgetLayout>
    </PageLayout>
  );
};

Addons.propTypes = {
  booking: bookingPropTypes.isRequired,
  experiences: experiencesPropTypes.isRequired,
  updateBookingStep: PropTypes.func.isRequired,
  updateBookingSubStep: PropTypes.func.isRequired,
  addonGroups: addonGroupsPropTypes.isRequired,
  beforeDetails: PropTypes.bool.isRequired,
  nextStep: PropTypes.number.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  experiences: state.experiences,
  addonGroups: state.addonGroups,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateBookingStep: updateBookingStepAction,
      updateBookingSubStep: updateBookingSubStepAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Addons);
